import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video,
  Calendar,
  Clock,
  Users,
  Plus,
  ExternalLink,
  Trash2,
  Edit2,
  X,
  Check,
  AlertCircle,
  Play,
  Settings
} from 'lucide-react';

const LiveSessionsPage = ({ user }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduled_date: '',
    scheduled_time: '',
    duration: 60,
    session_type: 'general',
    target_audience: 'all'
  });

  const isAdmin = user?.role === 'admin' || user?.is_admin;
  const isMentor = user?.role === 'mentor' || user?.is_mentor;
  const canManage = isAdmin || isMentor;

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const endpoint = canManage ? '/api/live-sessions/all' : '/api/live-sessions/upcoming';
      const response = await fetch(`${backendUrl}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      // Send as naive London-local datetime; backend interprets as Europe/London
      const scheduledTime = `${formData.scheduled_date}T${formData.scheduled_time}:00`;
      
      const response = await fetch(`${backendUrl}/api/live-sessions/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          scheduled_time: scheduledTime,
          duration: parseInt(formData.duration),
          session_type: formData.session_type,
          target_audience: formData.target_audience
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert('Live session created successfully!');
        setShowCreateModal(false);
        setFormData({
          title: '',
          description: '',
          scheduled_date: '',
          scheduled_time: '',
          duration: 60,
          session_type: 'general',
          target_audience: 'all'
        });
        fetchSessions();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create session');
      }
    } catch (error) {
      setError('Failed to create session. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/live-sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSessions();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const handleJoinSession = async (session) => {
    // For admins/hosts, open start URL if available
    if (canManage && session.zoom_start_url) {
      window.open(session.zoom_start_url, '_blank');
    } else if (session.zoom_link) {
      window.open(session.zoom_link, '_blank');
    } else {
      alert('Zoom link not available');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/London'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/London',
      hour12: false
    });
  };

  const isSessionLive = (session) => {
    const now = new Date();
    const start = new Date(session.scheduled_time);
    const end = new Date(start.getTime() + session.duration * 60000);
    return now >= start && now <= end;
  };

  const isSessionUpcoming = (session) => {
    const now = new Date();
    const start = new Date(session.scheduled_time);
    const timeDiff = start - now;
    return timeDiff > 0 && timeDiff < 30 * 60000; // Within 30 minutes
  };

  const getSessionTypeBadge = (type) => {
    const styles = {
      general: 'bg-purple-100 text-purple-800',
      mentorship: 'bg-blue-100 text-blue-800',
      course: 'bg-green-100 text-green-800',
      qa: 'bg-orange-100 text-orange-800'
    };
    const labels = {
      general: 'General',
      mentorship: 'Mentorship',
      course: 'Course',
      qa: 'Q&A'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type] || styles.general}`}>
        {labels[type] || type}
      </span>
    );
  };

  const getAudienceBadge = (audience) => {
    const labels = {
      all: 'Everyone',
      mentorship: 'Mentorship Members',
      'apostolic-prophetic': 'Apostolic-Prophetic Track',
      'minstrel-worship': 'Minstrel & Worship Track'
    };
    return (
      <span className="text-xs text-gray-500">
        {labels[audience] || audience}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading live sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="live-sessions-title">
              Live Sessions
            </h1>
            <p className="text-gray-600 mt-1">
              Join live Zoom sessions with Pastor Obed and the community
            </p>
          </div>
          
          {canManage && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              data-testid="create-session-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Schedule Session
            </button>
          )}
        </div>

        {/* Sessions Grid */}
        {sessions.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden ${
                  session.is_cancelled ? 'opacity-60' : ''
                } ${isSessionLive(session) ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-100'}`}
              >
                {/* Live/Upcoming Badge */}
                {isSessionLive(session) && !session.is_cancelled && (
                  <div className="bg-green-500 text-white text-center py-1 text-sm font-medium flex items-center justify-center">
                    <span className="animate-pulse mr-2">●</span> LIVE NOW
                  </div>
                )}
                {isSessionUpcoming(session) && !isSessionLive(session) && !session.is_cancelled && (
                  <div className="bg-orange-500 text-white text-center py-1 text-sm font-medium">
                    Starting Soon
                  </div>
                )}
                {session.is_cancelled && (
                  <div className="bg-red-500 text-white text-center py-1 text-sm font-medium">
                    Cancelled
                  </div>
                )}

                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-2">
                        {session.title}
                      </h3>
                      <div className="flex items-center space-x-2">
                        {getSessionTypeBadge(session.session_type)}
                      </div>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Video className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>

                  {/* Description */}
                  {session.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {session.description}
                    </p>
                  )}

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      {formatDate(session.scheduled_time)}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="w-4 h-4 mr-2 text-gray-400" />
                      {formatTime(session.scheduled_time)} • {session.duration} min
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="w-4 h-4 mr-2 text-gray-400" />
                      {getAudienceBadge(session.target_audience)}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 pt-4 border-t border-gray-100">
                    {!session.is_cancelled && (
                      <button
                        onClick={() => handleJoinSession(session)}
                        disabled={!session.zoom_link}
                        className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-colors ${
                          isSessionLive(session)
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        data-testid={`join-session-${session.id}`}
                      >
                        {canManage ? (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            {isSessionLive(session) ? 'Start Meeting' : 'Start as Host'}
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {isSessionLive(session) ? 'Join Now' : 'Join Meeting'}
                          </>
                        )}
                      </button>
                    )}
                    
                    {canManage && (
                      <button
                        onClick={() => handleDeleteSession(session.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Session"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
            <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Live Sessions Scheduled</h3>
            <p className="text-gray-500 mb-6">
              {canManage 
                ? 'Create your first live session to connect with your mentees.'
                : 'Check back later for upcoming live sessions with Pastor Obed.'}
            </p>
            {canManage && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-5 h-5 mr-2" />
                Schedule Your First Session
              </button>
            )}
          </div>
        )}

        {/* Create Session Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreateModal(false)} />
            
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Schedule Live Session</h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleCreateSession} className="p-6 space-y-4">
                {error && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    {error}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Weekly Mentorship Call"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="What will this session cover?"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time *
                    </label>
                    <input
                      type="time"
                      required
                      value={formData.scheduled_time}
                      onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Type
                  </label>
                  <select
                    value={formData.session_type}
                    onChange={(e) => setFormData({...formData, session_type: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="general">General Session</option>
                    <option value="mentorship">Mentorship Session</option>
                    <option value="course">Course Session</option>
                    <option value="qa">Q&A Session</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Who Can Join
                  </label>
                  <select
                    value={formData.target_audience}
                    onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">Everyone</option>
                    <option value="mentorship">Mentorship Members Only</option>
                    <option value="apostolic-prophetic">Apostolic-Prophetic Track Only</option>
                    <option value="minstrel-worship">Minstrel & Worship Track Only</option>
                  </select>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                  >
                    {creating ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Create Session
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveSessionsPage;
